// My Dashboard — the account manager's operating cockpit (V2).
// Expandable full-width bars over the person spine. A lens, not a store:
// every bar reads live from its owning collection, filtered to the viewer.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Layout from "../../components/Layout";
import Person360Overlay from "../../components/Person360Overlay";
import ActivityLogModal from "../../components/activity/ActivityLogModal";
import Notepad from "../../components/dashboard/Notepad";
import QuoteQuickPopup from "../../components/dashboard/QuoteQuickPopup";
import { useAuth } from "../../context/AuthContext";
import { metaFor, activityToneCls } from "../../components/activity/activityMeta";
import TradeSignupsBar from "./TradeSignupsBar";
import { toast } from "sonner";
import { WorkspaceTaskPopup, RhythmPill } from "./WorkspaceBars";
import DoNext from "./DoNext";
import { useCompanyWork, CheckinsCard, MyTasksCard, TeamPoolCard } from "./CompanyRail";
import ModuleFrame from "./ModuleFrame";
import LayoutToolbar from "./LayoutToolbar";
import { useDashLayout } from "./useDashLayout";
import { workspaceApi, TASK_CHANGED, isMgmt } from "../workspace/_shared";
import { useSearchParams } from "react-router-dom";
import { dueInfo, rowUrgencyCls, DueBadge, Bang, AlertPill, expiryInfo, daysSince, coldCls, StaleFlag } from "./urgency";
import {
  Users, FileText, ClipboardList, AlertTriangle, Hand, Bug, Activity, Timer,
  RefreshCw, ArrowRight, ChevronDown, ChevronRight, Crown, Loader2, Phone,
  Plus, Check, Pencil, Search, Sprout, StickyNote, ArrowUpDown, X,
  PhoneCall,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const nm = (v) => (Array.isArray(v) ? v.filter(Boolean).join(" ") : (v || ""));
const money = (n) => (n == null ? "—" : `£${Number(n).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`);
const dateShort = (iso) => {
  if (!iso || iso === "n/a") return "—";
  const d = new Date(iso);
  if (Number.isNaN(+d)) return "—";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", ...(d.getFullYear() !== new Date().getFullYear() && { year: "numeric" }) });
};
const relTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(+d)) return "—";
  const days = Math.round((Date.now() - +d) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "1d";
  if (days < 30) return `${days}d`;
  if (days < 365) return `${Math.round(days / 30)}mo`;
  return `${Math.round(days / 365)}y`;
};
const tierCls = (t) => {
  const k = String(t || "").toLowerCase();
  if (k.includes("black")) return "bg-slate-900 text-slate-100 border-slate-500/40";
  if (k.includes("plat")) return "bg-slate-200/10 text-slate-100 border-slate-300/40";
  if (k.includes("gold")) return "bg-amber-500/15 text-amber-300 border-amber-500/40";
  if (k.includes("silver")) return "bg-slate-400/15 text-slate-200 border-slate-400/40";
  if (k.includes("bronze")) return "bg-orange-800/20 text-orange-300 border-orange-700/40";
  return "border-white/10 text-slate-400";
};
const prioCls = (p) => {
  const k = String(p || "").toLowerCase();
  if (k === "high" || k === "urgent") return "text-rose-300";
  if (k === "low") return "text-slate-400";
  return "text-amber-300";
};

function Chip({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
        active ? "bg-teal-500/15 border-teal-400/50 text-teal-200"
               : "bg-white/[0.03] border-white/10 text-slate-400 hover:border-white/25"}`}>
      {children}
    </button>
  );
}

function Th({ label, k, sort, setSort }) {
  const active = sort.key === k;
  return (
    <th className="text-left px-3 py-2 font-mono uppercase tracking-wider text-[9.5px] text-slate-400 whitespace-nowrap select-none">
      <button type="button" onClick={() => setSort({ key: k, dir: active && sort.dir === "asc" ? "desc" : "asc" })}
        className="inline-flex items-center gap-1 hover:text-slate-200">
        {label}{active && <ArrowUpDown className="w-3 h-3 text-teal-300" />}
      </button>
    </th>
  );
}

function useSortable(rows, def) {
  const [sort, setSort] = useState(def || { key: "", dir: "asc" });
  const sorted = useMemo(() => {
    if (!sort.key) return rows;
    const s = [...rows].sort((a, b) => {
      const va = a[sort.key]; const vb = b[sort.key];
      if (va == null) return 1; if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return va - vb;
      return String(va).localeCompare(String(vb));
    });
    return sort.dir === "desc" ? s.reverse() : s;
  }, [rows, sort]);
  return [sorted, sort, setSort];
}

// Daily overdue-activity nudge — grouped by customer, biggest tier/spend first.
function OverdueDigest({ digest, totalCustomers, onOverlay, onAction }) {
  const [collapsed, setCollapsed] = useState(false);
  if (!digest || digest.length === 0) return null;
  const totalItems = digest.reduce((n, g) => n + (g.count || 0), 0);
  return (
    <div className="rounded-xl border border-rose-500/30 bg-rose-500/[0.06]" data-testid="overdue-digest">
      <button type="button" onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left" data-testid="overdue-digest-toggle">
        <AlertTriangle className="w-4 h-4 text-rose-300 shrink-0" />
        <span className="text-[13px] font-semibold text-rose-100">
          {totalItems} overdue {totalItems === 1 ? "activity" : "activities"} to chase
        </span>
        <span className="text-[11px] text-rose-300/70 font-mono">· {totalCustomers || digest.length} customer{(totalCustomers || digest.length) === 1 ? "" : "s"}</span>
        <ChevronDown className={`w-4 h-4 text-rose-300 ml-auto transition-transform ${collapsed ? "-rotate-90" : ""}`} />
      </button>
      {!collapsed && (
        <div className="px-3 pb-3 space-y-2" data-testid="overdue-digest-body">
          {digest.map((g) => (
            <div key={g.person_id} className="rounded-lg border border-white/[0.07] bg-black/20 p-2.5" data-testid={`overdue-digest-group-${g.person_id}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <button type="button" onClick={() => onOverlay(g.person_id)} data-testid={`overdue-digest-open-${g.person_id}`}
                  className="text-[12.5px] font-medium text-white hover:text-teal-300 truncate">{g.customer}</button>
                {g.tier && <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium border ${tierCls(g.tier)}`}>{g.tier}</span>}
                {g.ltv ? <span className="text-[10px] font-mono text-emerald-300/80">{money(g.ltv)}</span> : null}
                <span className="text-[10px] font-mono text-rose-300/80 ml-auto">{g.count} overdue</span>
              </div>
              <div className="space-y-1">
                {g.items.map((it) => {
                  const M = metaFor(it.activity_type); const Icon = M.icon;
                  return (
                    <div key={it.id} className="flex items-center gap-2 px-2 py-1 rounded bg-white/[0.02] border border-white/[0.05]" data-testid={`overdue-digest-item-${it.id}`}>
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium border ${activityToneCls(it.activity_type)}`}><Icon className="w-2.5 h-2.5" />{it.activity_type}</span>
                      <span className="text-[12px] text-slate-200 truncate flex-1">{it.subject}</span>
                      <span className="text-[10px] font-mono text-rose-300 shrink-0">{it.days_over != null ? `${it.days_over}d over` : dateShort(it.due_date)}</span>
                      <button type="button" onClick={() => onAction(it.id, "done")} data-testid={`overdue-digest-done-${it.id}`} title="Mark done"
                        className="p-1 rounded border border-white/10 bg-white/[0.03] hover:border-emerald-400/50 text-emerald-300 shrink-0"><Check className="w-3 h-3" /></button>
                      <button type="button" onClick={() => onAction(it.id, "cancelled")} data-testid={`overdue-digest-cancel-${it.id}`} title="Cancel"
                        className="p-1 rounded border border-white/10 bg-white/[0.03] hover:border-rose-400/50 text-slate-400 shrink-0"><X className="w-3 h-3" /></button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function Bar({ icon: Icon, tone, title, count, summary, open, onToggle, toolbar, children, footer, testid, alert }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid={testid}>
      <button type="button" onClick={onToggle} data-testid={`${testid}-toggle`}
        className="dash-sec-head w-full flex items-center gap-3 px-4 py-3 text-left transition-colors">
        {open ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
        <Icon className={`w-4 h-4 shrink-0 ${tone}`} />
        <span className="text-sm font-semibold text-slate-100">{title}</span>
        <span className="text-[11px] font-mono text-slate-400 tabular-nums" data-testid={`${testid}-count`}>{count}</span>
        {alert}
        {!open && summary && <span className="ml-auto text-[11.5px] text-slate-400 truncate">{summary}</span>}
      </button>
      {open && (
        <div className="border-t border-white/[0.06]">
          {toolbar && <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-white/[0.04]">{toolbar}</div>}
          <div className="overflow-x-auto">{children}</div>
          {footer && <div className="px-4 py-2 border-t border-white/[0.04] text-[11px] text-slate-400 flex items-center justify-between">{footer}</div>}
        </div>
      )}
    </div>
  );
}

function SearchBox({ value, onChange, testid }) {
  return (
    <div className="relative ml-auto">
      <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Search…" data-testid={testid}
        className="pl-8 pr-3 py-1.5 rounded-md bg-white/[0.03] border border-white/10 focus:border-teal-400/50 outline-none text-[12px] text-slate-200 w-44" />
    </div>
  );
}

const Empty = ({ children }) => <div className="px-4 py-6 text-center text-[12.5px] text-slate-500">{children}</div>;
const td = "px-3 py-2 text-[12.5px] text-slate-300 whitespace-nowrap";

export default function MyDashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [cockpit, setCockpit] = useState(null);
  const [amList, setAmList] = useState([]);
  const [reconcile, setReconcile] = useState(null);
  const [custData, setCustData] = useState({ items: [], total: 0 });
  const [sp] = useSearchParams();
  const [viewAs, setViewAs] = useState(sp.get("as") || "");
  const [meSum, setMeSum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overlayPid, setOverlayPid] = useState(null);
  const [openQuote, setOpenQuote] = useState(null);
  const [signupCount, setSignupCount] = useState(null);
  const [callCount, setCallCount] = useState(null);
  useEffect(() => { const h = (e) => setOpenQuote(e.detail); window.addEventListener("dash:open-quote", h); return () => window.removeEventListener("dash:open-quote", h); }, []);
  const [composer, setComposer] = useState(null); // {personId, defaultAssigneeName}
  const [editAct, setEditAct] = useState(null);
  const [openTask, setOpenTask] = useState(null);
  const [open, setOpen] = useState({ donext: true, customers: true, activities: true, quotes: true, outbound: true, notepad: true, dev: true });
  const [busyAct, setBusyAct] = useState({});
  const [editing, setEditing] = useState(false);
  const [dragId, setDragId] = useState(null);
  const dl = useDashLayout();

  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  const asQ = viewAs ? `?as=${encodeURIComponent(viewAs)}` : "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, c, am] = await Promise.all([
        axios.get(`${API}/me/dashboard`, { withCredentials: true }),
        axios.get(`${API}/me/cockpit${asQ}`, { withCredentials: true }),
        axios.get(`${API}/me/account-managers`, { withCredentials: true }),
      ]);
      setSummary(d.data); setCockpit(c.data); setAmList(am.data.account_managers || []);
      axios.get(`${API}/deals/reconcile`, { withCredentials: true }).then((r) => setReconcile(r.data)).catch(() => {});
      const cq = viewAs ? `?as=${encodeURIComponent(viewAs)}&limit=100` : "?limit=100";
      axios.get(`${API}/me/customers${cq}`, { withCredentials: true }).then((r) => setCustData(r.data)).catch(() => setCustData({ items: [], total: 0 }));
    } finally { setLoading(false); }
  }, [asQ, viewAs]);

  useEffect(() => { load(); }, [load]);
  const viewUser = useMemo(() => (viewAs ? amList.find((a) => a.name === viewAs) : null), [viewAs, amList]);
  const loadSum = useCallback(() => workspaceApi.summary(viewUser?.id || null).then(setMeSum).catch(() => {}), [viewUser]);
  useEffect(() => { loadSum(); }, [loadSum]);
  useEffect(() => { window.addEventListener(TASK_CHANGED, loadSum); return () => window.removeEventListener(TASK_CHANGED, loadSum); }, [loadSum]);
  const cw = useCompanyWork(viewUser?.id || null);

  const isManager = cockpit?.is_manager;
  const toggleActivityDone = async (a) => {
    setBusyAct((b) => ({ ...b, [a.id]: true }));
    try {
      await axios.patch(`${API}/crm/activities/${a.id}`, { completed: !a.completed }, { withCredentials: true });
      setCockpit((c) => ({ ...c, activities: c.activities.map((x) => x.id === a.id ? { ...x, completed: !a.completed, bucket: !a.completed ? "done" : "planned" } : x) }));
    } finally { setBusyAct((b) => ({ ...b, [a.id]: false })); }
  };
  const digestAction = async (id, status) => {
    await axios.patch(`${API}/crm/activities/${id}`, { status }, { withCredentials: true });
    load();
  };

  const tidyRan = useRef(false);
  const countsRef = useRef({});
  const dlRef = useRef(dl);
  dlRef.current = dl;
  useEffect(() => {
    if (tidyRan.current || !cockpit || !dl.metaLoaded || viewAs) return;
    tidyRan.current = true;
    setTimeout(() => {
      const d = dlRef.current;
      const prev = d.weeklyTidy(countsRef.current);
      if (prev) toast("Weekly tidy: empty boxes moved down, heights fitted", { action: { label: "Undo", onClick: () => dlRef.current.undoTidy(prev) }, duration: 8000 });
    }, 4000);
  }, [cockpit, dl.metaLoaded, viewAs]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && !cockpit) {
    return <Layout><div className="flex items-center justify-center py-32 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading your dashboard…</div></Layout>;
  }

  const s = summary?.stats || {};
  const salesUser = (custData.total || 0) > 0 || (cockpit?.quotes_owned || []).length > 0 || (cockpit?.activities || []).length > 0;
  const ms = meSum || {};
  const tiles = [
    { k: "do_next", label: "Do next", n: (ms.do_next || []).length, sub: "items ranked", tone: (ms.do_next || []).length ? "text-amber-300" : "text-slate-400", bar: "do-next" },
    { k: "overdue", label: "Overdue", n: (ms.activities?.overdue || 0) + (ms.tasks?.overdue || 0), sub: `${ms.activities?.overdue || 0} activities · ${ms.tasks?.overdue || 0} tasks`, tone: "text-rose-300", bar: "activities" },
    ...(salesUser ? [
      { k: "activities_today", label: "Activities today", n: ms.activities?.today || 0, sub: "", tone: "text-sky-300", bar: "activities" },
      { k: "quotes_expiring", label: "Quotes expiring ≤3d", n: (ms.quotes?.expiring_3d || 0), sub: `${money(ms.quotes?.open_value)} open`, tone: "text-orange-300", bar: "quotes" },
    ] : [
      { k: "checkins_due", label: "Routines due", n: ms.checkins?.due || 0, sub: "now", tone: "text-teal-300", bar: "rail-checkins" },
      { k: "due_week", label: "Due this week", n: ms.tasks?.due_week || 0, sub: "", tone: "text-sky-300", bar: "rail-my-tasks" },
    ]),
    { k: "my_tasks", label: "My tasks", n: ms.tasks?.open || 0, sub: `${ms.checkins?.due || 0} routine${ms.checkins?.due === 1 ? "" : "s"} due`, tone: "text-indigo-300", bar: "rail-my-tasks" },
    { k: "team_pool", label: "Team pool", n: ms.tasks?.pool || 0, sub: "claimable", tone: "text-fuchsia-300", bar: "rail-team-pool" },
  ];
  const jump = (bar) => { setOpen((o) => ({ ...o, [bar]: true })); setTimeout(() => document.querySelector(`[data-testid="${bar.startsWith("rail-") || bar === "do-next" ? bar : `bar-${bar}`}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); };
  const reloadAll = () => { load(); loadSum(); };
  const onLogP = (pid) => setComposer({ personId: pid, defaultAssigneeName: viewAs || nm(user?.name) });
  const MODS = {
    donext: { title: "Do next", render: (m) => <DoNext items={ms.do_next || []} salesUser={salesUser} open={open.donext} onToggle={() => toggle("donext")}
      onChanged={reloadAll} onOverlay={setOverlayPid} onOpenTask={setOpenTask} userMap={cw.userMap} teamMap={cw.teamMap} compact={m.w === "half"} /> },
    mytasks: { title: "My tasks", render: () => <MyTasksCard cw={cw} onOpenTask={setOpenTask} onNewTask={() => setOpenTask("new")} /> },
    pool: { title: "Team pool", render: () => <TeamPoolCard cw={cw} onOpenTask={setOpenTask} /> },
    checkins: { title: "Routines", render: () => <CheckinsCard cw={cw} onOpenTask={setOpenTask} /> },
    notepad: { title: "Notepad", render: () => <Bar icon={StickyNote} tone="text-yellow-300" title="Notepad" count="" open={open.notepad}
      onToggle={() => toggle("notepad")} testid="bar-notepad" summary="Quick sticky notes (this browser only)"><div className="p-4"><Notepad /></div></Bar> },
    customers: { title: "My Customers", sales: true, render: () => <CustomersBar open={open.customers} onToggle={() => toggle("customers")} data={custData}
      onOverlay={setOverlayPid} onLog={(p) => onLogP(p.id)} /> },
    activities: { title: "Activities", sales: true, render: () => <ActivitiesBar open={open.activities} onToggle={() => toggle("activities")} rows={cockpit?.activities || []}
      busy={busyAct} onDone={toggleActivityDone} onEdit={setEditAct} onOverlay={setOverlayPid} onLog={() => setComposer({ personId: null })} /> },
    quotes: { title: "My Quotes", sales: true, render: () => <QuotesBar open={open.quotes} onToggle={() => toggle("quotes")}
      owned={cockpit?.quotes_owned || []} collab={cockpit?.quotes_collaborating || []} onOverlay={setOverlayPid} onOpenQuote={setOpenQuote} /> },
    outbound: { title: "Outbound", sales: true, render: () => <OutboundBar open={open.outbound} onToggle={() => toggle("outbound")} rows={cockpit?.outbound || []}
      onOverlay={setOverlayPid} onLog={(r) => onLogP(r.person_id)} /> },
    callweek: { title: "Call this week", sales: true, render: () => <CallThisWeekBar am={viewAs} open={open.callweek ?? true} onToggle={() => toggle("callweek")}
      onOverlay={setOverlayPid} onLog={onLogP} onCount={setCallCount} /> },
    signups: { title: "Trade sign-ups", sales: true, render: () => <TradeSignupsBar am={viewAs} onOverlay={setOverlayPid} onCount={setSignupCount} /> },
    dev: { title: "Dev Tracker", show: (cockpit?.dev_tracker || []).length > 0, render: () => <DevBar open={open.dev} onToggle={() => toggle("dev")} rows={cockpit?.dev_tracker || []} /> },
  };
  const visibleMods = dl.layout.filter((m) => MODS[m.id] && (MODS[m.id].show ?? true) && (!MODS[m.id].sales || salesUser));
  const dashCounts = {
    donext: (ms.do_next || []).length, mytasks: cw.mine.length, pool: cw.pool.length, checkins: cw.checkins.length, notepad: 0,
    customers: custData.total || 0, activities: (cockpit?.activities || []).filter((a) => !a.completed).length,
    quotes: (cockpit?.quotes_owned || []).length + (cockpit?.quotes_collaborating || []).length, outbound: (cockpit?.outbound || []).length,
    signups: signupCount ?? 1, callweek: callCount ?? 1, dev: (cockpit?.dev_tracker || []).length,
  };
  countsRef.current = dashCounts;

  return (
    <Layout>
      <div className="max-w-[2200px] mx-auto px-4 sm:px-6 py-6 space-y-5 text-[15px]" data-testid="my-dashboard">
        {/* Header */}
        <div className="flex items-center gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-mono text-teal-300/70">My Dashboard</div>
            <h1 className="text-2xl font-display font-bold text-white">
              {viewAs ? `Viewing: ${viewAs}` : `Hi, ${nm(user?.name) || "there"}`}
            </h1>
          </div>
          <div className="ml-auto flex items-center gap-2 flex-wrap justify-end max-w-full">
            {isManager && isMgmt(user) && (
              <select value={viewAs} onChange={(e) => setViewAs(e.target.value)} data-testid="viewing-switcher"
                className="pl-3 pr-8 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-[12.5px] text-slate-200 cursor-pointer">
                <option value="">Viewing: Me</option>
                {amList.filter((a) => cockpit?.is_super_admin || !a.is_super_admin).map((a) => <option key={a.id || a.name} value={a.name}>{a.name}{a.is_manager ? " ★" : ""}</option>)}
              </select>
            )}
            <LayoutToolbar dl={dl} editing={editing} setEditing={setEditing} sales={salesUser} counts={dashCounts} />
            <button onClick={load} data-testid="dash-refresh" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] text-[12.5px] text-slate-200">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>

        {/* Alignment strip */}
        {reconcile && user?.role === "admin" && (
          <div className={`text-[11.5px] font-mono px-3 py-1.5 rounded-lg border inline-flex items-center gap-2 ${
            reconcile.aligned ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-amber-500/30 bg-amber-500/10 text-amber-300"}`}
            data-testid="alignment-strip">
            Deals {reconcile.deals_count ?? reconcile.deals ?? "—"} = Customers with orders {reconcile.customers_with_orders ?? reconcile.customers ?? "—"}
            · {reconcile.aligned ? "aligned" : "mismatch"}
            {!reconcile.aligned && <Link to="/deals" className="underline">reconcile</Link>}
          </div>
        )}

        {/* Overdue-activity digest — the daily chase nudge */}
        {!viewAs && <OverdueDigest digest={summary?.overdue_activity_digest || []}
          totalCustomers={s.overdue_digest_customers || 0}
          onOverlay={setOverlayPid} onAction={digestAction} />}

        {/* Stat tiles — all from the shared summary, lensed to the viewed user */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5" data-testid="stat-tiles">
          {tiles.map((t) => (
            <button key={t.k} onClick={() => jump(t.bar)} data-testid={`tile-${t.k}`}
              className="rounded-xl border border-white/10 bg-white/[0.02] p-3 text-left hover:border-white/25 transition-colors">
              <div className={`text-[9.5px] uppercase tracking-widest font-mono ${t.tone}`}>{t.label}</div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className={`text-2xl font-bold tabular-nums ${t.k === "overdue" && t.n ? "text-rose-300" : "text-white"}`} data-testid={`tile-${t.k}-count`}>{t.n}</span>
                {t.sub && <span className="text-[11px] text-slate-500 truncate">{t.sub}</span>}
              </div>
            </button>
          ))}
        </div>

        {dl.previewing && <div className="sticky top-2 z-30 flex flex-wrap items-center gap-3 rounded-lg border border-amber-400/50 bg-[#1a1408]/95 backdrop-blur px-3 py-2 text-[12.5px] text-amber-100 shadow-lg" data-testid="dash-optimise-preview">
          <span className="flex-1 min-w-[200px]">Optimised preview — busy boxes grew and moved up, empty ones shrank and moved down, side-by-side boxes share a height so there are no gaps.</span>
          <button onClick={() => { dl.cancelPreview(); }} className="px-3 py-1.5 rounded-lg border border-white/15 text-slate-200 hover:bg-white/[0.06]" data-testid="dash-optimise-cancel">Cancel</button>
          <button onClick={() => { dl.keepPreview(); toast.success("Layout saved"); }} className="px-3 py-1.5 rounded-lg bg-amber-400 text-slate-900 font-semibold hover:bg-amber-300" data-testid="dash-optimise-keep">Keep</button>
        </div>}

        {editing && <div className="rounded-lg border border-teal-400/30 bg-teal-500/[0.06] px-3 py-2 text-[12px] text-teal-100" data-testid="dash-customise-hint">
          Drag boxes by the handle or use the arrows to reorder. Switch each box between full and half width and pick its height. Boxes can't be removed, and changes save to your account.
        </div>}

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start" data-testid="dash-modules">
          {visibleMods.map((m, i) => (
            <ModuleFrame key={m.id} mod={m} index={i} total={visibleMods.length} title={MODS[m.id].title}
              editing={editing} dragId={dragId} setDragId={setDragId} onPatch={dl.patch}
              onMove={(id, vi) => { const t = visibleMods[vi]; if (t) dl.move(id, dl.layout.findIndex((x) => x.id === t.id)); }}>
              {MODS[m.id].render(m)}
            </ModuleFrame>
          ))}
        </div>

        {isMgmt(user) && (
          <Link to="/management/tasks" className="inline-block rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-[12px] text-slate-300 hover:border-white/25" data-testid="dash-mgmt-link">
            Team SLA and task overwatch live in <span className="text-teal-300">Management</span>
          </Link>
        )}

        {/* Jump to */}
        <div className="flex flex-wrap gap-2 pt-1">
          <Link to="/deals" className="text-[12px] px-3 py-1.5 rounded-lg border border-white/10 bg-white/[0.03] text-slate-300 hover:border-white/25">Deal pipeline</Link>
          <Link to="/leads" className="text-[12px] px-3 py-1.5 rounded-lg border border-white/10 bg-white/[0.03] text-slate-300 hover:border-white/25">Lead pipeline</Link>
          <Link to="/quotes/new" className="text-[12px] px-3 py-1.5 rounded-lg border border-teal-400/40 bg-teal-500/10 text-teal-200 hover:bg-teal-500/20">New quote</Link>
        </div>
      </div>

      <WorkspaceTaskPopup taskId={openTask} onClose={() => setOpenTask(null)} onSaved={reloadAll} />
      {openQuote && <QuoteQuickPopup quoteId={openQuote.id} personId={openQuote.person_id} onClose={() => setOpenQuote(null)} onChanged={reloadAll} onOpen360={(pid) => { setOpenQuote(null); setOverlayPid(pid); }} />}
      {overlayPid && <Person360Overlay personId={overlayPid} onClose={() => setOverlayPid(null)} />}
      {composer && (
        <ActivityLogModal personId={composer.personId} defaultAssigneeName={composer.defaultAssigneeName}
          onClose={() => setComposer(null)} onSaved={() => { setComposer(null); load(); }} />
      )}
      {editAct && (
        <ActivityLogModal personId={editAct.person_id} activity={editAct} contextLabel={editAct.person_name}
          defaultAssigneeName={nm(user?.name)} onClose={() => setEditAct(null)} onSaved={() => { setEditAct(null); load(); }} />
      )}
    </Layout>
  );
}

const WorkBtns = ({ pid, onOverlay, onLog, onCall }) => (
  <div className="flex items-center gap-1 justify-end">
    <button onClick={(e) => { e.stopPropagation(); onOverlay(pid); }} data-testid="row-360" title="Open 360"
      className="px-2 py-1 rounded-md border border-white/10 bg-white/[0.03] hover:border-teal-400/50 text-[11px] text-teal-200">360</button>
    {onCall && <button onClick={(e) => { e.stopPropagation(); onCall(); }} title="Call (log)" className="p-1.5 rounded-md border border-white/10 bg-white/[0.03] hover:border-white/25 text-slate-300"><Phone className="w-3 h-3" /></button>}
    {onLog && <button onClick={(e) => { e.stopPropagation(); onLog(); }} data-testid="row-log" title="Log activity" className="p-1.5 rounded-md border border-white/10 bg-white/[0.03] hover:border-white/25 text-slate-300"><Plus className="w-3 h-3" /></button>}
  </div>
);

function CustomersBar({ open, onToggle, data, onOverlay, onLog }) {
  const [chip, setChip] = useState("all");
  const [q, setQ] = useState("");
  const rows = useMemo(() => (data.items || []).filter((r) => {
    if (chip === "needs" && !r.needs_me) return false;
    if (chip === "nofollow" && r.next_activity) return false;
    if (q && !`${nm(r.full_name)} ${r.company || ""} ${r.email || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [data, chip, q]);
  const [sorted, sort, setSort] = useSortable(rows, { key: "ltv", dir: "desc" });
  return (
    <Bar icon={Users} tone="text-teal-300" title="My Customers" count={data.total || 0} open={open} onToggle={onToggle}
      testid="bar-customers" summary={`${data.total || 0} in your book`}
      toolbar={<>
        <Chip active={chip === "all"} onClick={() => setChip("all")} testid="cust-chip-all">All</Chip>
        <Chip active={chip === "needs"} onClick={() => setChip("needs")} testid="cust-chip-needs">Needs action</Chip>
        <Chip active={chip === "nofollow"} onClick={() => setChip("nofollow")} testid="cust-chip-nofollow">No follow-up ({(data.items || []).filter((r) => !r.next_activity).length})</Chip>
        <SearchBox value={q} onChange={setQ} testid="cust-search" />
      </>}
      footer={<><span>{sorted.length} shown</span><Link to="/my-dashboard/customers" className="text-teal-300 hover:underline inline-flex items-center gap-1">Full list <ArrowRight className="w-3 h-3" /></Link></>}>
      {sorted.length === 0 ? <Empty>No customers match.</Empty> : (
        <table className="w-full">
          <thead className="bg-white/[0.02]"><tr>
            <Th label="Customer" k="full_name" sort={sort} setSort={setSort} />
            <Th label="Tier" k="vip_tier" sort={sort} setSort={setSort} />
            <Th label="LTV" k="ltv" sort={sort} setSort={setSort} />
            <Th label="Orders" k="orders_count" sort={sort} setSort={setSort} />
            <th className="px-3 py-2 text-left font-mono uppercase tracking-wider text-[9.5px] text-slate-400">Open quotes</th>
            <th className="px-3 py-2 text-left font-mono uppercase tracking-wider text-[9.5px] text-slate-400">Next activity</th>
            <Th label="Last order" k="last_order_date" sort={sort} setSort={setSort} />
            <th className="px-3 py-2" />
          </tr></thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.id} onClick={() => onOverlay(r.id)} data-testid={`cust-row-${r.id}`}
                className="border-t border-white/[0.04] hover:bg-white/[0.03] cursor-pointer">
                <td className={td}><span className="flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${r.needs_me ? "bg-rose-400" : "bg-emerald-400/60"}`} />
                  <span className="min-w-0 max-w-[220px]"><span className="block truncate text-slate-100 font-medium">{nm(r.full_name) || "—"}</span>{r.company && <span className="block truncate text-[11px] text-slate-500">{r.company}</span>}</span>
                </span></td>
                <td className={td}>{r.vip_tier ? <span className={`px-1.5 py-0.5 rounded text-[10px] border ${tierCls(r.vip_tier)}`}>{r.vip_tier}</span> : "—"}</td>
                <td className={`${td} tabular-nums`}>{money(r.ltv)}</td>
                <td className={`${td} tabular-nums`}>{r.orders_count ?? 0}</td>
                <td className={td}>{r.open_quotes?.count ? <span className="text-orange-300 tabular-nums">{r.open_quotes.count} · {money(r.open_quotes.value)}</span> : <span className="text-slate-600">—</span>}</td>
                <td className={td}>{r.next_activity
                  ? <span className="inline-flex items-center gap-1.5"><DueBadge info={dueInfo(r.next_activity.due_date)} testid={`cust-next-due-${r.id}`} /><span className="text-slate-400">{r.next_activity.type}</span></span>
                  : <span className="text-slate-600" data-testid={`cust-no-followup-${r.id}`}>—</span>}</td>
                <td className={td} data-testid={`cust-since-${r.id}`}><div>{dateShort(r.last_order_date)}</div>{daysSince(r.last_order_date) != null && <div className={`font-mono text-[10.5px] ${coldCls(daysSince(r.last_order_date))}`}>{daysSince(r.last_order_date)}d ago</div>}</td>
                <td className={td}><WorkBtns pid={r.id} onOverlay={onOverlay} onLog={() => onLog(r)} onCall={() => onLog(r)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Bar>
  );
}

const PRIO_RANK = { urgent: 0, high: 1, normal: 2, medium: 2, low: 3 };
function ActivitiesBar({ open, onToggle, rows, busy, onDone, onEdit, onOverlay, onLog }) {
  const [chip, setChip] = useState("all");
  const [limit, setLimit] = useState(25);
  const openRows = rows.filter((a) => !a.completed);
  const typeCounts = openRows.reduce((m, a) => ({ ...m, [a.activity_type]: (m[a.activity_type] || 0) + 1 }), {});
  const ACT_CHIPS = [["all", `All ${openRows.length}`], ...Object.entries(typeCounts).sort((a, b) => b[1] - a[1]).map(([t, n]) => [t, `${t} ${n}`]), ["done", "Done"]];
  const [q, setQ] = useState("");
  const filtered = rows.filter((a) => {
    if (chip === "done") return a.completed;
    if (chip !== "all" && a.activity_type !== chip) return false;
    if (chip !== "done" && a.completed) return false;
    if (q && !`${a.subject || ""} ${a.person_name || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }).map((a) => ({ ...a, _due: dueInfo(a.due_date, a.due_time, a.completed) }))
    .sort((a, b) => (a._due.sortKey - b._due.sortKey) || ((PRIO_RANK[String(a.priority).toLowerCase()] ?? 2) - (PRIO_RANK[String(b.priority).toLowerCase()] ?? 2)));
  const groups = [["overdue", "Overdue"], ["today", "Today"], ["planned", "Planned"]];
  const overdue = rows.filter((a) => a.bucket === "overdue" && !a.completed).length;
  const shownIds = new Set(filtered.slice(0, limit).map((a) => a.id));
  return (
    <Bar icon={Activity} tone="text-sky-300" title="Activities" count={rows.filter((a) => !a.completed).length} open={open} onToggle={onToggle}
      testid="bar-activities" summary={overdue ? `${overdue} overdue` : "nothing overdue"}
      alert={<AlertPill count={overdue} testid="bar-activities-alert" />}
      toolbar={<>
        {ACT_CHIPS.map(([k, l]) => <Chip key={k} active={chip === k} onClick={() => setChip(k)} testid={`act-chip-${k}`}>{l}</Chip>)}
        <button onClick={() => onLog()} data-testid="act-log-new" className="text-[11px] px-2.5 py-1 rounded-full border border-teal-400/40 bg-teal-500/10 text-teal-200 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Log activity</button>
        <SearchBox value={q} onChange={setQ} testid="act-search" />
      </>}
      footer={<><span>{Math.min(limit, filtered.length)} of {filtered.length} shown</span>
        {filtered.length > limit && <button type="button" onClick={() => setLimit((l) => l + 25)} data-testid="act-show-more" className="text-teal-300 hover:underline">Show 25 more</button>}</>}>
      {filtered.length === 0 ? <Empty>No activities — a clear runway.</Empty> : (
        <table className="w-full">
          <thead className="bg-white/[0.02]"><tr>
            <th className="px-3 py-2 font-mono uppercase tracking-wider text-[9.5px] text-slate-400 text-left">Type</th>
            <th className="px-3 py-2 font-mono uppercase tracking-wider text-[9.5px] text-slate-400 text-left">Subject</th>
            <th className="px-3 py-2 font-mono uppercase tracking-wider text-[9.5px] text-slate-400 text-left">Customer</th>
            <th className="px-3 py-2 font-mono uppercase tracking-wider text-[9.5px] text-slate-400 text-left">Due</th>
            <th className="px-3 py-2 font-mono uppercase tracking-wider text-[9.5px] text-slate-400 text-left">Priority</th>
            <th className="px-3 py-2" />
          </tr></thead>
          <tbody>
            {groups.map(([gk, gl]) => {
              const g = filtered.filter((a) => shownIds.has(a.id) && (chip === "done" ? true : a.bucket === gk));
              if (chip === "done" || g.length === 0) return null;
              return (
                <GroupRows key={gk} label={gl} rows={g} busy={busy} onDone={onDone} onEdit={onEdit} onOverlay={onOverlay} />
              );
            })}
            {chip === "done" && filtered.slice(0, limit).map((a) => <ActRow key={a.id} a={a} busy={busy} onDone={onDone} onEdit={onEdit} onOverlay={onOverlay} />)}
          </tbody>
        </table>
      )}
    </Bar>
  );
}
function GroupRows({ label, rows, ...rest }) {
  return (<>
    <tr className="bg-white/[0.03]"><td colSpan={6} className="px-3 py-1 text-[10px] font-mono uppercase tracking-widest text-slate-400">{label} · {rows.length}</td></tr>
    {rows.map((a) => <ActRow key={a.id} a={a} {...rest} />)}
  </>);
}
function ActRow({ a, busy, onDone, onEdit, onOverlay }) {
  const Icon = metaFor(a.activity_type).icon;
  return (
    <tr className={`border-t border-white/[0.04] hover:bg-white/[0.03] ${a.completed ? "opacity-50" : rowUrgencyCls(a._due?.level)}`} data-testid={`act-row-${a.id}`}>
      <td className={td}><span className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[10.5px] border ${activityToneCls(a.activity_type)}`}><Icon className="w-3 h-3" />{a.activity_type}</span></td>
      <td className={`${td} max-w-[280px] truncate text-slate-100`} title={a.subject}>{a.subject || "—"}{a.linked_ref && <span className="text-slate-500"> · {a.linked_ref}</span>}</td>
      <td className={td}>{a.person_id ? <button onClick={() => onOverlay(a.person_id)} className="text-teal-300 hover:underline">{a.person_name}</button> : "—"}</td>
      <td className={td}><span className="inline-flex items-center gap-1.5">{dateShort(a.due_date)}{a.due_time ? ` ${a.due_time}` : ""}<DueBadge info={a._due} testid={`act-due-${a.id}`} /></span></td>
      <td className={`${td} ${prioCls(a.priority)}`}>{a.priority || "—"}</td>
      <td className={td}><div className="flex items-center gap-1 justify-end">
        <button onClick={() => onEdit(a)} data-testid={`act-edit-${a.id}`} title="Edit" className="p-1.5 rounded-md border border-white/10 bg-white/[0.03] hover:border-white/25 text-slate-300"><Pencil className="w-3 h-3" /></button>
        <button onClick={() => onDone(a)} data-testid={`act-done-${a.id}`} title={a.completed ? "Reopen" : "Mark done"} disabled={busy[a.id]}
          className={`p-1.5 rounded-md border ${a.completed ? "border-emerald-400/50 bg-emerald-500/15 text-emerald-300" : "border-white/10 bg-white/[0.03] text-slate-300 hover:border-emerald-400/50"}`}>
          {busy[a.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
        </button>
      </div></td>
    </tr>
  );
}

function QuotesBar({ open, onToggle, owned, collab, onOverlay, onOpenQuote }) {
  const [chip, setChip] = useState("owned");
  const base = chip === "collab" ? collab : owned;
  const rows = chip === "expiring"
    ? [...owned, ...collab].filter((q) => q.expiry && (new Date(q.expiry) - Date.now()) / 86400000 <= 3)
    : base;
  const [sorted, sort, setSort] = useSortable(rows, { key: "updated", dir: "desc" });
  return (
    <Bar icon={FileText} tone="text-orange-300" title="My Quotes" count={owned.length + collab.length} open={open} onToggle={onToggle}
      testid="bar-quotes" summary={`${owned.length} owned · ${collab.length} collaborating`}
      alert={<AlertPill count={[...owned, ...collab].filter((q) => expiryInfo(q.expiry)?.level === "overdue").length} label="expiring/expired" testid="bar-quotes-alert" />}
      toolbar={<>
        <Chip active={chip === "owned"} onClick={() => setChip("owned")} testid="quote-chip-owned">Owned</Chip>
        <Chip active={chip === "collab"} onClick={() => setChip("collab")} testid="quote-chip-collab">Collaborating</Chip>
        <Chip active={chip === "expiring"} onClick={() => setChip("expiring")} testid="quote-chip-expiring">Expiring ≤3d</Chip>
      </>}
      footer={<><span>{sorted.length} shown</span><Link to="/quotes" className="text-teal-300 hover:underline">View Quotes</Link></>}>
      {sorted.length === 0 ? <Empty>No quotes here.</Empty> : (
        <table className="w-full">
          <thead className="bg-white/[0.02]"><tr>
            <Th label="Ref" k="quote_ref" sort={sort} setSort={setSort} />
            <Th label="Customer" k="customer_name" sort={sort} setSort={setSort} />
            <Th label="Value" k="total" sort={sort} setSort={setSort} />
            <Th label="Stage" k="status" sort={sort} setSort={setSort} />
            <Th label="Expires" k="expiry" sort={sort} setSort={setSort} />
            <th className="px-3 py-2" />
          </tr></thead>
          <tbody>
            {sorted.map((q) => (
              <tr key={q.id} onClick={() => onOpenQuote(q)} className={`cursor-pointer border-t border-white/[0.04] hover:bg-white/[0.03] ${rowUrgencyCls(expiryInfo(q.expiry)?.level)}`} data-testid={`quote-row-${q.id}`}>
                <td className={`${td} font-mono text-slate-100`}>{q.quote_ref || "—"}</td>
                <td className={td}>{q.customer_name || <span className="text-amber-300">no customer</span>}</td>
                <td className={`${td} tabular-nums`}>{money(q.total)}</td>
                <td className={td}><span className="inline-flex items-center gap-1.5">{q.status}<StaleFlag days={daysSince(q.updated)} testid={`quote-stale-${q.id}`} /></span></td>
                <td className={td}><span className="inline-flex items-center gap-1.5">{dateShort(q.expiry)}<DueBadge info={expiryInfo(q.expiry)} testid={`quote-expiry-${q.id}`} /></span></td>
                <td className={td}><div className="flex items-center gap-1 justify-end">
                  {q.person_id && <button onClick={() => onOverlay(q.person_id)} data-testid={`quote-360-${q.id}`} className="px-2 py-1 rounded-md border border-white/10 bg-white/[0.03] hover:border-teal-400/50 text-[11px] text-teal-200">360</button>}
                  <button onClick={() => onOpenQuote(q)} data-testid={`quote-open-${q.id}`} className="px-2 py-1 rounded-md border border-white/10 bg-white/[0.03] hover:border-white/25 text-[11px] text-slate-300">Open</button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Bar>
  );
}

const OB_CHIPS = [["all", "All"], ["no_order_60", "No order 60d+"], ["no_order_90", "No order 90d+"], ["lapsing_tier", "Lapsing tier"], ["high_ltv_dormant", "High-LTV dormant"], ["never_ordered", "Never ordered"]];
function CallThisWeekBar({ am, open, onToggle, onOverlay, onLog, onCount }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}/me/call-this-week${am ? `?as=${encodeURIComponent(am)}` : ""}`, { withCredentials: true }).then(({ data }) => { setD(data); onCount?.(data.items.length); }).catch(() => setD({ items: [] }));
  }, [am]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows = d?.items || [];
  return (
    <Bar icon={PhoneCall} tone="text-amber-300" title="Call this week" count={rows.length} open={open} onToggle={onToggle} testid="bar-callweek"
      summary="Top-value customers who've gone Cooling — slowing big spenders" footer={<span>{d?.total ?? 0} Cooling · Top value in your book · <Link to="/crm/velocity" className="text-teal-300 hover:underline">Sales Velocity →</Link></span>}>
      {!d ? <Empty>Loading…</Empty> : rows.length === 0 ? <Empty>No top-value customers are cooling off — nice work.</Empty> : (
        <table className="w-full">
          <thead className="bg-white/[0.02]"><tr>{["Customer", "Last order", "Spend 12m", "Orders 12m", ""].map((h) => <th key={h} className="px-3 py-2 text-left font-mono uppercase tracking-wider text-[9.5px] text-slate-400">{h}</th>)}</tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id} className="border-t border-white/[0.04] hover:bg-white/[0.03]" data-testid={`callweek-row-${r.id}`}>
              <td className={td}><button onClick={() => onOverlay(r.id)} className="text-teal-300 hover:underline">{nm(r.name)}</button>{r.company && <span className="text-slate-500"> · {r.company}</span>}</td>
              <td className={td}>{dateShort(r.LastOrderDate)} <span className="text-slate-500 text-[11px]">{r.DaysSinceLast}d ago</span></td>
              <td className={`${td} tabular-nums text-white`}>{money(r.Spend12m)}</td>
              <td className={`${td} tabular-nums`}>{r.Orders12m}</td>
              <td className={`${td} text-right`}>
                <div className="inline-flex gap-1">
                  {r.phone && <a href={`tel:${r.phone}`} className="px-2 py-1 rounded-md border border-amber-400/40 bg-amber-500/10 text-[11px] text-amber-200" data-testid={`callweek-call-${r.id}`}>Call</a>}
                  <button onClick={() => onLog(r.id)} className="px-2 py-1 rounded-md border border-white/10 bg-white/[0.03] text-[11px] text-slate-300" data-testid={`callweek-log-${r.id}`}>Log</button>
                  <button onClick={() => onOverlay(r.id)} className="px-2 py-1 rounded-md border border-teal-400/40 bg-teal-500/10 text-[11px] text-teal-200" data-testid={`callweek-360-${r.id}`}>360</button>
                </div>
              </td>
            </tr>))}</tbody>
        </table>)}
    </Bar>
  );
}

function OutboundBar({ open, onToggle, rows, onOverlay, onLog }) {
  const [chip, setChip] = useState("all");
  const filtered = rows.filter((r) => chip === "all" || r.signal === chip);
  const [sorted, sort, setSort] = useSortable(filtered, { key: "ltv", dir: "desc" });
  return (
    <Bar icon={Sprout} tone="text-lime-300" title="Outbound — farm your book" count={rows.length} open={open} onToggle={onToggle}
      testid="bar-outbound" summary={`${rows.length} to work`}
      toolbar={<>{OB_CHIPS.map(([k, l]) => <Chip key={k} active={chip === k} onClick={() => setChip(k)} testid={`ob-chip-${k}`}>{l}</Chip>)}</>}
      footer={<span>{sorted.length} shown · rules-based cold queue</span>}>
      {sorted.length === 0 ? <Empty>Nothing cold — your book is warm. 🌱</Empty> : (
        <table className="w-full">
          <thead className="bg-white/[0.02]"><tr>
            <Th label="Customer" k="name" sort={sort} setSort={setSort} />
            <Th label="Tier" k="tier" sort={sort} setSort={setSort} />
            <Th label="LTV" k="ltv" sort={sort} setSort={setSort} />
            <Th label="Last order" k="last_order" sort={sort} setSort={setSort} />
            <th className="px-3 py-2 text-left font-mono uppercase tracking-wider text-[9.5px] text-slate-400">Why surfaced</th>
            <th className="px-3 py-2 text-left font-mono uppercase tracking-wider text-[9.5px] text-slate-400">Suggested play</th>
            <th className="px-3 py-2" />
          </tr></thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.id} className="border-t border-white/[0.04] hover:bg-white/[0.03]" data-testid={`ob-row-${r.id}`}>
                <td className={td}><button onClick={() => onOverlay(r.person_id)} className="text-teal-300 hover:underline">{nm(r.name)}</button>{r.company && <span className="text-slate-500"> · {r.company}</span>}</td>
                <td className={td}>{r.tier ? <span className={`px-1.5 py-0.5 rounded text-[10px] border ${tierCls(r.tier)}`}>{r.tier}</span> : "—"}</td>
                <td className={`${td} tabular-nums`}>{money(r.ltv)}</td>
                <td className={td}>{dateShort(r.last_order)}{r.days != null && <span className={`ml-1.5 font-mono text-[10.5px] ${coldCls(r.days)}`} data-testid={`ob-days-${r.id}`}>{r.days}d</span>}</td>
                <td className={`${td} text-amber-300/90`}>{r.why}</td>
                <td className={`${td} text-slate-400 max-w-[220px] truncate`} title={r.play}>{r.play}</td>
                <td className={td}><WorkBtns pid={r.person_id} onOverlay={onOverlay} onLog={() => onLog(r)} onCall={() => onLog(r)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Bar>
  );
}

function DevBar({ open, onToggle, rows }) {
  return (
    <Bar icon={Bug} tone="text-emerald-300" title="Dev Tracker — mine" count={rows.length} open={open} onToggle={onToggle}
      testid="bar-dev" summary={`${rows.length} open items`}
      footer={<Link to="/items/all" className="text-teal-300 hover:underline">Open Dev Tracker</Link>}>
      {rows.length === 0 ? <Empty>No dev items assigned to you.</Empty> : (
        <table className="w-full">
          <thead className="bg-white/[0.02]"><tr>{["Item", "Type", "Area", "Status", "Priority"].map((h) => <th key={h} className="px-3 py-2 text-left font-mono uppercase tracking-wider text-[9.5px] text-slate-400">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-white/[0.04] hover:bg-white/[0.03]" data-testid={`dev-row-${r.id}`}>
                <td className={`${td} text-slate-100 max-w-[360px] truncate`} title={r.title}>
                  {["high", "urgent", "critical"].includes(String(r.priority).toLowerCase()) && <span className="mr-1.5 align-middle" data-testid={`dev-flag-${r.id}`}><Bang title="High priority" /></span>}{r.title}</td>
                <td className={td}>{r.type || "—"}</td><td className={td}>{r.area || "—"}</td>
                <td className={td}>{r.status || "—"}</td><td className={`${td} ${prioCls(r.priority)}`}>{r.priority || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Bar>
  );
}

export function ManagerBar({ open, onToggle, rows, onView }) {
  return (
    <Bar icon={Crown} tone="text-amber-300" title="Manager overwatch — SLA" count={rows.length} open={open} onToggle={onToggle}
      testid="bar-sla" summary={`${rows.reduce((n, r) => n + (r.activities_overdue || 0), 0)} SLA breaches`}>
      {rows.length === 0 ? <Empty>All account managers within SLA.</Empty> : (
        <table className="w-full">
          <thead className="bg-white/[0.02]"><tr>{["Account manager", "Customers", "SLA overdue", "Sent", "Compliance", "Longest overdue", "Today's digest", ""].map((h) => <th key={h} className="px-3 py-2 text-left font-mono uppercase tracking-wider text-[9.5px] text-slate-400">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.am} className="border-t border-white/[0.04] hover:bg-white/[0.03]" data-testid={`sla-row-${String(r.am).replace(/[^a-z0-9]+/gi, "-")}`}>
                <td className={`${td} text-slate-100`}>{r.am}</td>
                <td className={`${td} tabular-nums`}>{r.customers}</td>
                <td className={`${td} tabular-nums ${r.activities_overdue ? "text-rose-300" : ""}`}>{r.activities_overdue}</td>
                <td className={`${td} tabular-nums`}>{r.sent}</td>
                <td className={`${td} tabular-nums ${r.compliance_pct != null && r.compliance_pct < 80 ? "text-amber-300" : "text-emerald-300"}`}>{r.compliance_pct == null ? "—" : `${r.compliance_pct}%`}</td>
                <td className={td}>{r.longest ? `${r.longest.quote_ref} · ${r.longest.customer} · ${r.longest.hours_over}h` : "—"}</td>
                <td className={td} data-testid={`sla-digest-${String(r.am).replace(/[^a-z0-9]+/gi, "-")}`}>
                  {r.digest === "acknowledged" ? <span className="text-emerald-300 text-[11px]">✓ acknowledged {r.digest_ack_at ? new Date(r.digest_ack_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                    : r.digest === "pending" ? <span className="inline-flex items-center gap-1 text-rose-300 text-[11px]"><Bang /> not acknowledged</span>
                    : <span className="text-slate-600 text-[11px]">—</span>}
                </td>
                <td className={td}><button onClick={() => onView(r.am)} data-testid={`sla-view-${String(r.am).replace(/[^a-z0-9]+/gi, "-")}`} className="px-2 py-1 rounded-md border border-white/10 bg-white/[0.03] hover:border-teal-400/50 text-[11px] text-teal-200">View patch</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Bar>
  );
}
